import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import React, { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, Modal } from 'react-bootstrap';
import clsx from 'clsx';
import { VuiButton } from '../../../../../vodea/@vodea-ui/components/VuiButton';
import VuiSelect from '../../../../../vodea/@vodea-ui/components/Forms/VuiSelect';
import MembershipRepository from '../../../../../repositories/MembershipRepository';
import _ from 'lodash';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import NumberFormat from 'react-number-format';
import { formatFormData } from '../../../../../vodea/utilities/helpers/form';
import CustomerRepository from '../../../../../repositories/CustomerRepository';
import { AxiosError, AxiosResponse } from 'axios';
import { mapHookErrors, showToast } from '../../../../../vodea/utilities/helpers/global';

interface Props {
  onSave: (data: any) => void;
}

export const schema = yup.object().shape({
  name: yup.string().required().label('Name'),
  limit_credit: yup.number().label('Limit credit'),
  membership_id: yup.mixed().required().label('Membership ID'),
});

const ButtonAddCustomer: React.FC<Props> = ({onSave}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    errors,
    reset,
    register,
    handleSubmit,
    control,
    setError
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      membership_id: null,
      limit_credit: 0
    }
  })

  const handleOnClose = useCallback(() => {
    setIsOpen(false);
    reset();
  }, []);

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true);
    const formData = formatFormData(data);

    await CustomerRepository.create(formData)
      .then((response: AxiosResponse) => {
        showToast(response.data.message, 'success');
        onSave(response.data.data);
        handleOnClose();
      })
      .catch((e: AxiosError) => {
        if (e?.response?.data?.errors) {
          const errors = mapHookErrors(e.response.data.errors);
          Object.keys(errors).forEach((key: any) => {
            setError(key, errors[key]);
          });
          showToast(e.response.data.message, 'error');
        }
      }).finally(() => {
        setIsLoading(false);
      });
  });

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  return (
    <>
      <Modal show={isOpen} onHide={handleOnClose} backdrop={'static'} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Customer</Modal.Title>
        </Modal.Header>

        <form className={'form-wrapper'} onSubmit={onSubmit}>
          <Modal.Body>
            <div
              className={clsx({
                'form-group': true,
                error: errors.name
              })}
            >
              <label className={'form-label'}>Name</label>
              <input type='text' name='name' className='form-control' ref={register} />
              <label className={'label-help'}>{errors.name?.message}</label>
            </div>

            <div
              className={clsx({
                'form-group': true,
                error: errors.membership_id
              })}
            >
              <label className={'form-label'}>Membership</label>
              <Controller
                name={'membership_id'}
                control={control}
                render={props => (
                  <VuiSelect
                    selectRepository={MembershipRepository}
                    defaultValue={props.value}
                    onChange={props.onChange}
                    isMulti={false}
                  />
                )}
              />
              <label className={'label-help'}>{_.get(errors.membership_id, 'message')}</label>
            </div>

            <div
              className={clsx({
                'form-group': true,
                error: errors.limit_credit
              })}
            >
              <label className={'form-label'}>Limit Credit</label>
              <Controller
                name={'limit_credit'}
                control={control}
                render={props => (
                  <NumberFormat
                    placeholder={'IDR 0'}
                    className='form-control'
                    value={props.value}
                    allowNegative={false}
                    thousandSeparator={'.'}
                    decimalSeparator={','}
                    decimalScale={0}
                    onValueChange={({ value }) => props.onChange(value)}
                    prefix={'IDR '}
                  />
                )}
              />
              <label className={'label-help'}>{errors.limit_credit?.message}</label>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant={'light'} onClick={handleOnClose}>
              Cancel
            </Button>
            <VuiButton forSubmit={true} label={'Save'} loading={isLoading} />
          </Modal.Footer>
        </form>
      </Modal>

      <div
        className='d-flex align-items-center justify-content-center'
        style={{
          borderRadius: 4,
          border: '1px solid black',
          width: 40,
          height: 36,
          cursor: 'pointer'
        }}
        onClick={handleOpen}
      >
        <FontAwesomeIcon icon={faPlus} />
      </div>
    </>
  );
}

export default ButtonAddCustomer;